import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import humanTime from 'flarum/common/helpers/humanTime';
import { api } from '../../common/utils/api';
import { formatMoney } from '../../common/utils/currency';
import MpSkeleton from './MpSkeleton';
import StoreVerifiedBadge from './StoreVerifiedBadge';
import { mpImage } from '../../common/utils/mpImage';
/**
 * Seção "Vendedor" embutida no perfil do usuário (substitui a página /loja/{slug}):
 * nota média, vitrine de produtos e as avaliações aprovadas. Quem comprou do
 * vendedor pode avaliar (uma vez, sujeito à aprovação do admin). Não renderiza
 * nada se o usuário não for um vendedor ativo.
 */
export default class SellerProfileSection extends Component {
  loading = false;
  notSeller = false;
  data = null;
  fetchedFor = 0;
  draftRating = 5;
  draftBody = '';
  draftProduct = null;
  sending = false;
  brokenImages = (() => new Set())();
  watchTimer = null;
  watchPolls = 0;
  // Handler unico (referencia estavel) p/ poder remover no onremove.
  visHandler = () => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') this.refreshVerified();
  };
  t(k, vars) {
    return app.translator.trans('ramon-marketplace.forum.seller_profile.' + k, vars);
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    // "Tempo real" sem push (o forum nao tem Pusher): re-le o status de
    // verificacao quando a aba volta ao foco — qualquer visitante ve o selo
    // aparecer ao voltar pra cá, logo apos o admin verificar.
    document.addEventListener('visibilitychange', this.visHandler);
    window.addEventListener('focus', this.visHandler);
    // E enquanto o PROPRIO dono olha o estande à espera da aprovacao, um poll
    // gentil faz o selo surgir "na hora", sem reload. Só o dono paga esse
    // custo; ele para sozinho quando verifica (refreshVerified) ou ao sair.
    this.watchTimer = setInterval(() => this.watchTick(), 25000);
  }
  onremove(vnode) {
    document.removeEventListener('visibilitychange', this.visHandler);
    window.removeEventListener('focus', this.visHandler);
    if (this.watchTimer) clearInterval(this.watchTimer);
    super.onremove?.(vnode);
  }

  /** O visitante atual é o dono deste estande? (vendedor olhando o próprio perfil) */
  isOwner() {
    const u = this.attrs.user;
    const me = app.session.user;
    return !!me && !!u && typeof u.id === 'function' && Number(me.id()) === Number(u.id());
  }
  watchTick() {
    // Só o dono, aba visível, ainda não verificado e dentro do teto (~16min).
    if (!this.data || this.data.store.verified || !this.isOwner()) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    if (this.watchPolls >= 40) return;
    this.watchPolls++;
    this.refreshVerified();
  }

  /** Re-lê só o selo da loja e o acende ao vivo. No-op se já verificado. */
  refreshVerified() {
    const u = this.attrs.user;
    if (!u || typeof u.id !== 'function' || !this.data || this.data.store.verified) return;
    api('GET', '/marketplace/sellers/by-user/' + Number(u.id())).then(r => {
      if (r?.data?.store?.verified && this.data && !this.data.store.verified) {
        this.data.store.verified = true;
        m.redraw();
      }
    }).catch(() => {
      /* offline/404: tenta de novo no próximo foco/tick */
    });
  }

  /** Carrega os dados do vendedor uma vez por usuário (o perfil carrega o user async). */
  ensureLoaded(userId) {
    if (!userId || this.fetchedFor === userId) return;
    this.fetchedFor = userId;
    this.loading = true;
    this.notSeller = false;
    this.data = null;
    api('GET', '/marketplace/sellers/by-user/' + userId).then(r => {
      this.data = r.data;
      if (this.data?.my_review) {
        this.draftRating = this.data.my_review.rating;
        this.draftBody = this.data.my_review.body ?? '';
      }
    }).catch(e => {
      if (e?.status === 404) this.notSeller = true;
    }).then(() => {
      this.loading = false;
      m.redraw();
    });
  }
  async submitReview() {
    if (!this.data) return;
    this.sending = true;
    m.redraw();
    try {
      await api('POST', `/marketplace/sellers/${this.data.store.id}/reviews`, {
        rating: this.draftRating,
        body: this.draftBody,
        product_id: this.draftProduct
      });
      app.alerts.show({
        type: 'success'
      }, this.t('sent'));
      this.draftProduct = null;
      this.fetchedFor = 0;
      this.ensureLoaded(Number(this.attrs.user?.id()));
    } catch (e) {
      const code = e?.response?.error === 'purchase_required' ? 'purchase_required' : 'review_failed';
      app.alerts.show({
        type: 'error'
      }, this.t(code));
    } finally {
      this.sending = false;
      m.redraw();
    }
  }
  async deleteReview() {
    if (!this.data?.my_review) return;
    if (!confirm(this.t('confirm_delete'))) return;
    const reviewId = this.data.my_review.id;
    this.sending = true;
    m.redraw();
    try {
      await api('DELETE', `/marketplace/seller-reviews/${reviewId}`);
      app.alerts.show({
        type: 'success'
      }, this.t('deleted'));
      this.draftRating = 5;
      this.draftBody = '';
      this.draftProduct = null;
      this.fetchedFor = 0;
      this.ensureLoaded(Number(this.attrs.user?.id()));
    } catch (e) {
      app.alerts.show({
        type: 'error'
      }, this.t('review_failed'));
    } finally {
      this.sending = false;
      m.redraw();
    }
  }
  stars(rating) {
    return m("span", {
      className: "MpSellerProfile-stars"
    }, [1, 2, 3, 4, 5].map(n => m("i", {
      key: n,
      className: 'fas fa-star' + (n <= rating ? ' is-on' : '')
    })));
  }
  view() {
    const user = this.attrs.user;
    if (!user || typeof user.id !== 'function') return null;
    this.ensureLoaded(Number(user.id()));

    // Carregando: mostra skeleton em vez de tela em branco.
    if (!this.data && this.loading && !this.notSeller) {
      return m("section", {
        className: "MpSellerProfile"
      }, m(MpSkeleton, {
        variant: "seller"
      }));
    }
    if (this.notSeller || !this.data) return null;
    const d = this.data;
    return m("section", {
      className: "MpSellerProfile MpSellerProfile--editorial"
    }, m("header", {
      className: "MpSellerProfile-id"
    }, m("span", {
      className: "MpSellerProfile-avatar"
    }, d.store.logo ? m("img", {
      src: this.logoUrl(d.store.logo),
      alt: d.store.store_name
    }) : m("i", {
      className: "fas fa-store"
    })), m("h2", {
      className: "MpSellerProfile-name"
    }, m("span", {
      className: "MpSellerProfile-nameText"
    }, d.store.store_name), d.store.verified && m(StoreVerifiedBadge, null)), m("div", {
      className: "MpSellerProfile-meta"
    }, this.stars(Math.round(d.store.rating_avg)), m("span", {
      className: "MpSellerProfile-ratingNum"
    }, d.store.rating_avg.toFixed(1), " \xB7 ", d.store.rating_count, " ", this.t('reviews_label'), d.store.member_since ? ` · ${this.t('member_since', {
      year: d.store.member_since
    })}` : '')), m("div", {
      className: "MpSellerProfile-statline"
    }, m("span", null, m("strong", null, d.products.length), " ", this.t('stat_products')), m("span", {
      className: "MpSellerProfile-statDot"
    }), m("span", null, m("strong", null, d.store.sales_count ?? 0), " ", this.t('stat_sales')), m("span", {
      className: "MpSellerProfile-statDot"
    }), m("span", null, m("strong", null, d.store.rating_avg.toFixed(1)), " ", this.t('stat_rating'))), d.store.bio && m("p", {
      className: "MpSellerProfile-bio"
    }, d.store.bio), !!app.session.user && !this.isOwner() && m("a", {
      href: app.route('marketplace.messages') + '?seller=' + d.store.id,
      config: m.route.link,
      className: "Button MpSellerProfile-contact"
    }, m("i", {
      className: "fas fa-comments"
    }), " ", this.t('contact_store'))), d.products.length > 0 && m("div", {
      className: "MpSellerProfile-block"
    }, m("div", {
      className: "MpSellerProfile-blockHead"
    }, m("h4", {
      className: "MpSellerProfile-subtitle"
    }, this.t('products_title')), m("span", {
      className: "MpSellerProfile-count"
    }, d.products.length)), m("div", {
      className: "MpSellerProfile-grid"
    }, d.products.map(p => this.renderProduct(p, p.id === this.featuredProductId())))), m("div", {
      className: "MpSellerProfile-block"
    }, m("div", {
      className: "MpSellerProfile-blockHead"
    }, m("h4", {
      className: "MpSellerProfile-subtitle"
    }, this.t('reviews_title')), d.reviews.length > 0 && m("span", {
      className: "MpSellerProfile-count"
    }, d.reviews.length)), d.can_review && this.renderForm(), d.reviews.length === 0 ? m("p", {
      className: "MpSellerProfile-empty"
    }, this.t('no_reviews')) : m("div", {
      className: "MpSellerProfile-reviews"
    }, d.reviews.map(r => this.renderReview(r)))));
  }

  /** URL do logo: filename do upload vira /assets/marketplace/...; URL/data fica como está. */
  logoUrl(logo) {
    return /^(https?:|data:|\/)/.test(logo) ? logo : '/assets/marketplace/' + logo;
  }

  /** Produto em destaque = o mais vendido (com ao menos 1 venda). */
  featuredProductId() {
    let id = 0;
    let best = 0;
    for (const p of this.data?.products ?? []) {
      const s = p.sales ?? 0;
      if (s > best) {
        best = s;
        id = p.id;
      }
    }
    return id;
  }
  renderProduct(p, featured) {
    return m("a", {
      key: p.id,
      href: app.route('marketplace.product', {
        id: p.id
      }),
      config: m.route.link,
      className: "MpSellerProfile-card"
    }, m("div", {
      className: "MpSellerProfile-cover"
    }, featured && m("span", {
      className: "MpSellerProfile-coverTag"
    }, this.t('featured')), p.image && !this.brokenImages.has(p.id) ? m("img", Object.assign({}, mpImage(p.image), {
      alt: p.name,
      onerror: () => {
        this.brokenImages.add(p.id);
        m.redraw();
      }
    })) : m("i", {
      className: "fas fa-box"
    })), m("div", {
      className: "MpSellerProfile-cardBody"
    }, m("span", {
      className: "MpSellerProfile-cardName"
    }, p.name), (p.rating || (p.sales ?? 0) > 0) && m("span", {
      className: "MpSellerProfile-cardMeta"
    }, p.rating ? m("span", {
      className: "MpSellerProfile-cardRate"
    }, m("i", {
      className: "fas fa-star"
    }), " ", p.rating.toFixed(1)) : null, p.rating && (p.sales ?? 0) > 0 ? m("span", {
      className: "MpSellerProfile-dot"
    }) : null, (p.sales ?? 0) > 0 ? m("span", null, this.t('sales_count', {
      count: p.sales
    })) : null), m("strong", {
      className: "MpSellerProfile-cardPrice"
    }, p.price > 0 ? formatMoney(p.price) : this.t('free'))));
  }
  renderForm() {
    return m("div", {
      className: "MpSellerProfile-form"
    }, m("h5", {
      className: "MpSellerProfile-formTitle"
    }, this.data?.my_review ? this.t('edit_review') : this.t('write_review')), m("div", {
      className: "MpSellerProfile-ratePick"
    }, [1, 2, 3, 4, 5].map(n => m("i", {
      key: n,
      className: 'fas fa-star' + (n <= this.draftRating ? ' is-on' : ''),
      onclick: () => this.draftRating = n
    }))), this.data?.my_purchases?.length ? m("label", {
      className: "MpSellerProfile-formField"
    }, m("span", {
      className: "MpSellerProfile-formLabel"
    }, this.t('review_product_label')), m("select", {
      className: "FormControl",
      value: String(this.draftProduct ?? ''),
      onchange: e => {
        const v = e.target.value;
        this.draftProduct = v ? Number(v) : null;
      }
    }, [m("option", {
      key: "__none",
      value: ""
    }, this.t('review_product_none')), ...this.data.my_purchases.map(p => m("option", {
      key: p.id,
      value: String(p.id)
    }, p.name))])) : null, m("textarea", {
      className: "FormControl MpSellerProfile-textarea",
      rows: 3,
      maxlength: 2000,
      placeholder: this.t('review_placeholder'),
      value: this.draftBody,
      oninput: e => this.draftBody = e.target.value
    }), m("div", {
      className: "MpSellerProfile-formActions"
    }, m(Button, {
      className: "Button Button--primary",
      loading: this.sending,
      onclick: () => this.submitReview()
    }, this.t('submit')), this.data?.my_review ? m(Button, {
      className: "Button Button--danger MpSellerProfile-deleteBtn",
      disabled: this.sending,
      onclick: () => this.deleteReview()
    }, this.t('delete_review')) : null), m("p", {
      className: "MpSellerProfile-formHint"
    }, this.t('moderation_hint')));
  }
  renderReview(r) {
    const name = r.author?.display_name || r.author?.username || '?';
    return m("div", {
      className: "MpSellerProfile-review",
      key: r.id
    }, m("span", {
      className: "MpSellerProfile-reviewAvatar"
    }, name.charAt(0).toUpperCase()), m("div", {
      className: "MpSellerProfile-reviewMain"
    }, m("div", {
      className: "MpSellerProfile-reviewTop"
    }, m("span", {
      className: "MpSellerProfile-reviewName"
    }, name), r.created_at && m("span", {
      className: "MpSellerProfile-reviewDate"
    }, humanTime(new Date(r.created_at))), this.stars(r.rating)), r.product_name && r.product_id ? m("a", {
      href: app.route('marketplace.product', {
        id: r.product_id
      }),
      config: m.route.link,
      className: "MpSellerProfile-reviewProduct"
    }, this.t('review_about_product', {
      product: r.product_name
    })) : null, r.body && m("p", {
      className: "MpSellerProfile-reviewText"
    }, r.body), r.seller_reply && m("div", {
      className: "MpSellerProfile-reply"
    }, m("span", {
      className: "MpSellerProfile-replyLabel"
    }, this.t('seller_reply_label')), m("p", null, r.seller_reply))));
  }
}
flarum.reg.add('ramon-marketplace', 'forum/components/SellerProfileSection', SellerProfileSection);